import { useState, useEffect, useRef } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import axios from 'axios';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward,
  Volume2,
  VolumeX,
  Search,
  Music,
  Clock,
  User,
  Shuffle,
  Repeat,
  List,
  X,
  FileJson,
  ExternalLink
} from 'lucide-react';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const categories = [
  { value: 'all', label: 'All Categories' },
  { value: 'meditation', label: 'Meditation & Mindfulness' },
  { value: 'motivation', label: 'Motivational' },
  { value: 'relaxation', label: 'Relaxation & Sleep' },
  { value: 'podcast', label: 'Podcasts' },
  { value: 'music', label: 'Background Music' },
  { value: 'affirmation', label: 'Affirmations' },
  { value: 'nature', label: 'Nature Sounds' },
  { value: 'other', label: 'Other' },
];

export default function AudioPage() {
  const { currentTheme } = useTheme();
  const [audioFiles, setAudioFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [catalogInfo, setCatalogInfo] = useState(null);
  
  // Player state
  const [currentTrack, setCurrentTrack] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.8);
  const [isMuted, setIsMuted] = useState(false);
  const [showPlaylist, setShowPlaylist] = useState(false);
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);
  
  const audioRef = useRef(null);

  useEffect(() => {
    fetchAudio();
  }, [category]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume;
    }
  }, [volume, isMuted]);

  const fetchAudio = async () => {
    try {
      const params = category !== 'all' ? `?category=${category}` : '';
      const [audioRes, catalogRes] = await Promise.all([
        axios.get(`${API}/audio${params}`),
        axios.get(`${API}/audio/catalog`).catch(() => ({ data: null }))
      ]);
      setAudioFiles(audioRes.data || []);
      if (catalogRes.data && catalogRes.data.generated_at) {
        setCatalogInfo(catalogRes.data);
      }
    } catch (error) {
      console.error('Error fetching audio:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredAudio = audioFiles.filter(item =>
    item.title.toLowerCase().includes(search.toLowerCase()) ||
    (item.artist && item.artist.toLowerCase().includes(search.toLowerCase()))
  );

  const playTrack = (track) => {
    if (currentTrack?.audio_id === track.audio_id && isPlaying) {
      // Same track and playing - pause it
      if (audioRef.current) {
        audioRef.current.pause();
      }
      return;
    }
    
    // Set the current track
    setCurrentTrack(track);
    
    // Increment play count (fire and forget)
    axios.post(`${API}/audio/${track.audio_id}/play`).catch(() => {});
    
    // Play the audio
    if (audioRef.current) {
      const audio = audioRef.current;
      const src = track.file_url.startsWith('/') 
        ? `${BACKEND_URL}${track.file_url}` 
        : track.file_url;
      
      // Reset state
      setCurrentTime(0);
      setDuration(0);
      
      audio.src = src;
      audio.volume = isMuted ? 0 : volume;
      
      // Wait for loadedmetadata before playing
      const onMetadataLoaded = () => {
        const dur = audio.duration;
        if (dur && isFinite(dur) && !isNaN(dur)) {
          setDuration(dur);
        }
        audio.play().catch(err => {
          console.error('Play failed:', err.name, err.message);
        });
        audio.removeEventListener('loadedmetadata', onMetadataLoaded);
      };
      
      audio.addEventListener('loadedmetadata', onMetadataLoaded);
      audio.load();
    }
  };

  const togglePlay = () => {
    if (!currentTrack || !audioRef.current) return;
    
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play().catch(err => {
        console.error('Toggle play failed:', err);
      });
    }
    // Note: isPlaying will be set by onPlay/onPause events
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) {
      const time = audioRef.current.currentTime;
      const dur = audioRef.current.duration;
      
      if (isFinite(time) && !isNaN(time)) {
        setCurrentTime(time);
      }
      if (dur && isFinite(dur) && !isNaN(dur)) {
        setDuration(dur);
      }
    }
  };

  const handleSeek = (value) => {
    if (audioRef.current && duration) {
      audioRef.current.currentTime = (value[0] / 100) * duration;
      setCurrentTime(audioRef.current.currentTime);
    }
  };

  const handleVolumeChange = (value) => {
    const newVolume = value[0] / 100;
    setVolume(newVolume);
    setIsMuted(newVolume === 0);
  };

  const handleEnded = () => {
    if (repeat) {
      audioRef.current.currentTime = 0;
      audioRef.current.play();
    } else {
      playNext();
    }
  };

  const playNext = () => {
    if (!currentTrack || filteredAudio.length === 0) return;
    
    const currentIndex = filteredAudio.findIndex(a => a.audio_id === currentTrack.audio_id);
    let nextIndex;
    
    if (shuffle) {
      do {
        nextIndex = Math.floor(Math.random() * filteredAudio.length);
      } while (nextIndex === currentIndex && filteredAudio.length > 1);
    } else {
      nextIndex = (currentIndex + 1) % filteredAudio.length;
    }
    
    playTrack(filteredAudio[nextIndex]);
  };

  const playPrevious = () => {
    if (!currentTrack || filteredAudio.length === 0) return;
    
    const currentIndex = filteredAudio.findIndex(a => a.audio_id === currentTrack.audio_id);
    const prevIndex = currentIndex === 0 ? filteredAudio.length - 1 : currentIndex - 1;
    playTrack(filteredAudio[prevIndex]);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCategoryLabel = (value) => {
    return categories.find(c => c.value === value)?.label || value;
  };

  return (
    <div className="min-h-screen pb-32" style={{ backgroundColor: currentTheme.background }}>
      <Navbar />
      
      {/* Audio element */}
      <audio
        ref={audioRef}
        preload="metadata"
        onTimeUpdate={handleTimeUpdate}
        onEnded={handleEnded}
        onLoadedMetadata={(e) => {
          const dur = e.target.duration;
          if (dur && isFinite(dur) && !isNaN(dur)) {
            setDuration(dur);
          }
        }}
        onDurationChange={(e) => {
          const dur = e.target.duration;
          if (dur && isFinite(dur) && !isNaN(dur)) {
            setDuration(dur);
          }
        }}
        onError={(e) => {
          console.error('Audio error:', e.target.error?.message || 'Unknown error');
          setIsPlaying(false);
        }}
        onPlay={() => setIsPlaying(true)}
        onPause={() => setIsPlaying(false)}
      />
      
      <main className="pt-28 pb-16 px-4 md:px-8">
        <div className="container mx-auto max-w-7xl">
          {/* Header */}
          <div className="mb-12">
            <h1 className="font-display text-4xl md:text-5xl font-medium mb-4" style={{ color: currentTheme.foreground }}>
              Audio Library
            </h1>
            <p className="font-body text-lg opacity-70 max-w-2xl">
              Listen to meditations, affirmations, relaxing sounds, and more to support your wellness journey.
            </p>
          </div>

          {/* JSON Catalog Link */}
          {catalogInfo && catalogInfo.generated_at && (
            <div 
              className="mb-8 p-4 rounded-xl flex items-center justify-between flex-wrap gap-4"
              style={{ 
                backgroundColor: 'white',
                boxShadow: '0 2px 10px rgba(0,0,0,0.05)'
              }}
            >
              <div className="flex items-center gap-3">
                <div 
                  className="w-10 h-10 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${currentTheme.primary}15` }}
                >
                  <FileJson className="w-5 h-5" style={{ color: currentTheme.primary }} />
                </div>
                <div>
                  <p className="font-body font-medium" style={{ color: currentTheme.foreground }}>
                    Audio Catalog (JSON)
                  </p>
                  <p className="text-sm opacity-60">
                    {catalogInfo.total_files} files • Updated {new Date(catalogInfo.generated_at).toLocaleDateString()}
                  </p>
                </div>
              </div>
              <a
                href={`${BACKEND_URL}/api/uploads/audio_catalog.json`}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg font-medium transition-colors hover:opacity-80"
                style={{ 
                  backgroundColor: currentTheme.primary,
                  color: 'white'
                }}
                data-testid="view-catalog-link"
              >
                <ExternalLink className="w-4 h-4" />
                View JSON Catalog
              </a>
            </div>
          )}

          {/* Filters */}
          <div className="flex flex-col md:flex-row gap-4 mb-8">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 opacity-40" />
              <Input
                placeholder="Search audio..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-12 h-12"
                data-testid="audio-search"
              />
            </div>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger className="w-full md:w-[250px] h-12" data-testid="audio-category-filter">
                <SelectValue placeholder="Category" />
              </SelectTrigger>
              <SelectContent>
                {categories.map((cat) => (
                  <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Audio Grid */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="skeleton h-24 rounded-2xl" />
              ))}
            </div>
          ) : filteredAudio.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredAudio.map((audio) => (
                <div
                  key={audio.audio_id}
                  className={`flex items-center gap-4 p-4 rounded-2xl cursor-pointer transition-all hover:scale-[1.02] ${
                    currentTrack?.audio_id === audio.audio_id ? 'ring-2' : ''
                  }`}
                  style={{ 
                    backgroundColor: currentTrack?.audio_id === audio.audio_id 
                      ? `${currentTheme.primary}15` 
                      : 'white',
                    boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                    ringColor: currentTheme.primary
                  }}
                  onClick={() => playTrack(audio)}
                  data-testid={`audio-track-${audio.audio_id}`}
                >
                  {/* Thumbnail */}
                  <div 
                    className="relative w-16 h-16 rounded-xl flex-shrink-0 flex items-center justify-center overflow-hidden"
                    style={{ backgroundColor: `${currentTheme.primary}20` }}
                  >
                    {audio.thumbnail_url ? (
                      <img 
                        src={`${BACKEND_URL}${audio.thumbnail_url}`} 
                        alt={audio.title}
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <Music className="w-8 h-8" style={{ color: currentTheme.primary }} />
                    )}
                    
                    {/* Play indicator */}
                    {currentTrack?.audio_id === audio.audio_id && isPlaying && (
                      <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                        <div className="flex gap-1">
                          <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '0ms' }} />
                          <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '150ms' }} />
                          <div className="w-1 h-4 bg-white rounded-full animate-pulse" style={{ animationDelay: '300ms' }} />
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <h3 className="font-body font-medium truncate" style={{ color: currentTheme.foreground }}>
                      {audio.title}
                    </h3>
                    <div className="flex items-center gap-3 mt-1 text-sm opacity-60">
                      <span className="flex items-center gap-1 truncate">
                        <User className="w-3 h-3 flex-shrink-0" />
                        {audio.artist}
                      </span>
                      <span className="flex items-center gap-1">
                        <Clock className="w-3 h-3" />
                        {audio.duration}
                      </span>
                    </div>
                    <span 
                      className="inline-block px-2 py-0.5 rounded-full text-xs mt-2"
                      style={{ backgroundColor: `${currentTheme.secondary}20`, color: currentTheme.secondary }}
                    >
                      {getCategoryLabel(audio.category)}
                    </span>
                  </div>

                  {/* Play button */}
                  <div 
                    className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: currentTheme.primary }}
                  >
                    {currentTrack?.audio_id === audio.audio_id && isPlaying ? (
                      <Pause className="w-5 h-5 text-white" />
                    ) : (
                      <Play className="w-5 h-5 text-white ml-0.5" />
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Music className="w-16 h-16 mx-auto mb-4 opacity-30" />
              <h3 className="font-display text-xl font-medium mb-2" style={{ color: currentTheme.foreground }}>
                No audio found
              </h3>
              <p className="font-body opacity-60">
                {search ? 'Try adjusting your search' : 'Check back later for new content'}
              </p>
            </div>
          )}
        </div>
      </main>

      {/* Fixed Player Bar */}
      {currentTrack && (
        <div 
          className="fixed bottom-0 left-0 right-0 z-50 border-t"
          style={{ 
            backgroundColor: currentTheme.foreground,
            borderColor: `${currentTheme.border}50`
          }}
        >
          {/* Progress bar */}
          <div 
            className="h-1 cursor-pointer group"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const percent = ((e.clientX - rect.left) / rect.width) * 100;
              handleSeek([percent]);
            }}
          >
            <div 
              className="h-full transition-all group-hover:h-2"
              style={{ 
                width: `${duration ? (currentTime / duration) * 100 : 0}%`,
                backgroundColor: currentTheme.primary
              }}
            />
          </div>

          <div className="container mx-auto px-4 md:px-8">
            <div className="flex items-center justify-between py-4">
              {/* Track Info */}
              <div className="flex items-center gap-4 flex-1 min-w-0">
                <div 
                  className="w-14 h-14 rounded-xl flex-shrink-0 overflow-hidden"
                  style={{ backgroundColor: `${currentTheme.primary}20` }}
                >
                  {currentTrack.thumbnail_url ? (
                    <img 
                      src={`${BACKEND_URL}${currentTrack.thumbnail_url}`} 
                      alt={currentTrack.title}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <Music className="w-6 h-6" style={{ color: currentTheme.primary }} />
                    </div>
                  )}
                </div>
                <div className="min-w-0">
                  <h4 className="font-medium text-white truncate">{currentTrack.title}</h4>
                  <p className="text-sm text-white/60 truncate">{currentTrack.artist}</p>
                </div>
              </div>

              {/* Controls */}
              <div className="flex items-center gap-4">
                <button 
                  onClick={() => setShuffle(!shuffle)}
                  className={`hidden md:block p-2 rounded-full transition-colors ${shuffle ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                  style={{ color: shuffle ? currentTheme.primary : undefined }}
                >
                  <Shuffle className="w-5 h-5" />
                </button>
                
                <button 
                  onClick={playPrevious}
                  className="p-2 text-white/60 hover:text-white transition-colors"
                >
                  <SkipBack className="w-6 h-6" />
                </button>
                
                <button 
                  onClick={togglePlay}
                  className="w-12 h-12 rounded-full flex items-center justify-center"
                  style={{ backgroundColor: currentTheme.primary }}
                >
                  {isPlaying ? (
                    <Pause className="w-6 h-6 text-white" />
                  ) : (
                    <Play className="w-6 h-6 text-white ml-1" />
                  )}
                </button>
                
                <button 
                  onClick={playNext}
                  className="p-2 text-white/60 hover:text-white transition-colors"
                >
                  <SkipForward className="w-6 h-6" />
                </button>
                
                <button 
                  onClick={() => setRepeat(!repeat)}
                  className={`hidden md:block p-2 rounded-full transition-colors ${repeat ? 'text-white' : 'text-white/40 hover:text-white/60'}`}
                  style={{ color: repeat ? currentTheme.primary : undefined }}
                >
                  <Repeat className="w-5 h-5" />
                </button>
              </div>

              {/* Time & Volume */}
              <div className="hidden md:flex items-center gap-4 flex-1 justify-end">
                <span className="text-sm text-white/60 w-20 text-right">
                  {formatTime(currentTime)} / {formatTime(duration)}
                </span>
                
                <div className="flex items-center gap-2">
                  <button 
                    onClick={() => setIsMuted(!isMuted)}
                    className="p-2 text-white/60 hover:text-white transition-colors"
                  >
                    {isMuted || volume === 0 ? (
                      <VolumeX className="w-5 h-5" />
                    ) : (
                      <Volume2 className="w-5 h-5" />
                    )}
                  </button>
                  <div className="w-24">
                    <Slider
                      value={[isMuted ? 0 : volume * 100]}
                      onValueChange={handleVolumeChange}
                      max={100}
                      step={1}
                      className="cursor-pointer"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {!currentTrack && <Footer />}
    </div>
  );
}
